import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './SignIn.css';

function Register() {
    const [registerData, setRegisterData] = useState({
        email: "",
        fullName: "",
        phone: "",
        password: "",
        confirmPassword: ""
    });

    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState(0);
    const navigate = useNavigate();

    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password)) strength += 15;
        if (/[^a-zA-Z0-9]/.test(password)) strength += 10;
        return Math.min(strength, 100);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "phone") {
            let formattedValue = value.replace(/\D/g, "");

            if (formattedValue.startsWith("994")) {
                formattedValue = formattedValue.substring(3);
            }

            if (formattedValue.length > 9) {
                formattedValue = formattedValue.substring(0, 9);
            }

            setRegisterData((prevData) => ({
                ...prevData,
                phone: formattedValue
            }));
            return;
        }

        if (name === "password") {
            setPasswordStrength(calculatePasswordStrength(value));
        }

        setRegisterData((prevData) => ({ ...prevData, [name]: value }));
        setErrorMessage("");
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 30) return '#ef4444';
        if (passwordStrength < 60) return '#f59e0b';
        if (passwordStrength < 80) return '#3b82f6';
        return '#10b981';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength < 30) return 'Zəif';
        if (passwordStrength < 60) return 'Orta';
        if (passwordStrength < 80) return 'Yaxşı';
        return 'Güclü';
    };

    const handleRegister = async (e) => {
        e.preventDefault();

        if (!registerData.email || !registerData.fullName || !registerData.phone || 
            !registerData.password || !registerData.confirmPassword) {
            setErrorMessage("Bütün xanalara məlumat daxil edin!");
            return toast.warn("⚠️ Bütün xanalara məlumat daxil edin!");
        }

        if (registerData.phone.length !== 9) {
            setErrorMessage("Mobil nömrə 9 rəqəmdən ibarət olmalıdır!");
            return toast.error("❌ Mobil nömrə 9 rəqəmdən ibarət olmalıdır!");
        }

        if (registerData.password.length < 8) {
            setErrorMessage("Şifrə minimum 8 simvol olmalıdır!");
            return toast.error("❌ Şifrə minimum 8 simvol olmalıdır!");
        }

        if (registerData.password !== registerData.confirmPassword) {
            setErrorMessage("Şifrələr uyğun gəlmir!");
            return toast.error("❌ Şifrələr uyğun gəlmir!");
        }

        setLoading(true);

        try {
            const formattedPhone = `+994${registerData.phone}`;

            const payload = {
                email: registerData.email,
                fullName: registerData.fullName,
                phoneNumber: formattedPhone,
                password: registerData.password,
                confirmPassword: registerData.confirmPassword
            };

            console.log("📤 Göndərilən məlumat:", payload);

            const response = await axios.post(
                'https://api.herracim.az/api/Account/register',
                payload,
                {
                    headers: { 'Content-Type': 'application/json' },
                    timeout: 15000
                }
            );

            console.log("✅ Backend cavabı:", response.data);

            if (response.status === 200 || response.status === 201) {
                toast.success("🎉 Qeydiyyat uğurla tamamlandı!");

                setTimeout(() => {
                    navigate("/giris", {
                        state: {
                            message: "Qeydiyyat tamamlandı! İndi giriş edə bilərsiniz.",
                            email: registerData.email
                        }
                    });
                }, 2000);
            }
        } catch (error) {
            console.error("🚨 Tam xəta:", error);
            console.error("🚨 Response data:", error.response?.data);
            console.error("🚨 Response status:", error.response?.status);

            if (error.response) {
                const errorData = error.response.data;
                const errorMsg = typeof errorData === "string"
                    ? errorData
                    : errorData?.message || "Serverdə xəta baş verdi";

                toast.error(`❌ ${errorMsg}`, { autoClose: 5000 });
                setErrorMessage(errorMsg);

                if (errorData?.details) {
                    console.error("📋 Ətraflı məlumat:", errorData.details);
                }
                if (errorData?.innerException) {
                    console.error("📋 Inner Exception:", errorData.innerException);
                }
            } else if (error.code === 'ECONNABORTED') {
                toast.error("⏱️ Sorğu vaxtı bitdi. Yenidən cəhd edin.");
                setErrorMessage("Sorğu vaxtı bitdi.");
            } else if (error.code === 'ERR_NETWORK') {
                toast.error("🌐 Serverlə bağlantı qurula bilmədi.");
                setErrorMessage("Server əlçatan deyil.");
            } else {
                toast.error("❌ Gözlənilməz xəta baş verdi.");
                setErrorMessage("Gözlənilməz xəta.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className='herrac-register-wrapper'>
            <div className='herrac-register-overlay'></div>
            
            <div className='herrac-register-container'>
                <Link to="/" className='herrac-register-close' aria-label='Bağla'>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </Link>

                <div className="herrac-register-card">
                    <div className='herrac-register-header'>
                        <div className='herrac-register-icon'>
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                <circle cx="24" cy="24" r="22" stroke="url(#gradient2)" strokeWidth="2"/>
                                <path d="M24 20C26.2091 20 28 18.2091 28 16C28 13.7909 26.2091 12 24 12C21.7909 12 20 13.7909 20 16C20 18.2091 21.7909 20 24 20Z" stroke="url(#gradient2)" strokeWidth="2"/>
                                <path d="M16 34C16 29.5817 19.5817 26 24 26C28.4183 26 32 29.5817 32 34" stroke="url(#gradient2)" strokeWidth="2" strokeLinecap="round"/>
                                <defs>
                                    <linearGradient id="gradient2" x1="0" y1="0" x2="48" y2="48">
                                        <stop offset="0%" stopColor="#f093fb"/>
                                        <stop offset="100%" stopColor="#f5576c"/>
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <h2 className='herrac-register-title'>Yeni Hesab Yarat</h2>
                        <p className='herrac-register-subtitle'>Bir neçə saniyədə qoşulun</p>
                    </div>

                    <form className="herrac-register-form" onSubmit={handleRegister} noValidate>
                        <div className='herrac-register-input-group'>
                            <label className='herrac-register-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M2.5 5.83333L10 10.8333L17.5 5.83333M3.33333 15H16.6667C17.5871 15 18.3333 14.2538 18.3333 13.3333V6.66667C18.3333 5.74619 17.5871 5 16.6667 5H3.33333C2.41286 5 1.66667 5.74619 1.66667 6.66667V13.3333C1.66667 14.2538 2.41286 15 3.33333 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                E-mail ünvanı
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={registerData.email}
                                onChange={handleInputChange}
                                placeholder='example@mail.com'
                                className='herrac-register-input'
                                autoComplete='email'
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className='herrac-register-input-group'>
                            <label className='herrac-register-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 10C12.0711 10 13.75 8.32107 13.75 6.25C13.75 4.17893 12.0711 2.5 10 2.5C7.92893 2.5 6.25 4.17893 6.25 6.25C6.25 8.32107 7.92893 10 10 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M3.75 17.5C3.75 14.0482 6.54822 11.25 10 11.25C13.4518 11.25 16.25 14.0482 16.25 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Ad və soyad
                            </label>
                            <input
                                type="text"
                                name="fullName"
                                value={registerData.fullName}
                                onChange={handleInputChange}
                                placeholder='Adınız və soyadınız'
                                className='herrac-register-input'
                                autoComplete='name'
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className='herrac-register-input-group'>
                            <label className='herrac-register-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15.8333 3.75H4.16667C3.24619 3.75 2.5 4.49619 2.5 5.41667V14.5833C2.5 15.5038 3.24619 16.25 4.16667 16.25H15.8333C16.7538 16.25 17.5 15.5038 17.5 14.5833V5.41667C17.5 4.49619 16.7538 3.75 15.8333 3.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M6.66667 7.5V12.5M10 7.5V12.5M13.3333 7.5V12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Mobil nömrə
                            </label>
                            <div className="herrac-register-phone-wrapper">
                                <span className="herrac-register-phone-prefix">+994</span>
                                <input
                                    type="text"
                                    name="phone"
                                    value={registerData.phone}
                                    onChange={handleInputChange}
                                    placeholder='50 123 45 67'
                                    className='herrac-register-input herrac-register-phone-input'
                                    autoComplete='tel'
                                    required
                                    disabled={loading}
                                />
                            </div>
                        </div>

                        <div className='herrac-register-input-group'>
                            <label className='herrac-register-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M5.83333 9.16667V5.83333C5.83333 4.72826 6.27232 3.66846 7.05372 2.88706C7.83512 2.10565 8.89493 1.66667 10 1.66667C11.1051 1.66667 12.1649 2.10565 12.9463 2.88706C13.7277 3.66846 14.1667 4.72826 14.1667 5.83333V9.16667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Şifrə
                            </label>
                            <div className='herrac-register-password-wrapper'>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    name="password"
                                    value={registerData.password}
                                    onChange={handleInputChange}
                                    placeholder='Minimum 8 simvol'
                                    className='herrac-register-input'
                                    autoComplete='new-password'
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type='button'
                                    className='herrac-register-password-toggle'
                                    onClick={() => setShowPassword(!showPassword)}
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M8.33333 8.33333C8.11232 8.65885 7.99609 9.04836 8.00008 9.44605C8.00407 9.84373 8.12806 10.2306 8.35531 10.5515C8.58256 10.8723 8.90151 11.1116 9.2695 11.2372C9.63749 11.3627 10.0346 11.3683 10.4058 11.2533M11.6667 11.6667C11.0844 12.0992 10.3609 12.2822 9.64002 12.178C8.91916 12.0739 8.26431 11.6903 7.80834 11.1066C7.35237 10.5229 7.13046 9.78413 7.19185 9.04086C7.25325 8.29759 7.59299 7.60648 8.14167 7.1M14.95 14.95C13.5255 16.0358 11.788 16.6374 10 16.6667C5 16.6667 2.5 10 2.5 10C3.45651 8.06825 4.81629 6.38051 6.475 5.05L14.95 14.95ZM8.25833 3.53333C8.82361 3.39907 9.4009 3.33195 9.98 3.33333C14.98 3.33333 17.4808 10 17.4808 10C17.0535 10.9463 16.5269 11.8423 15.9108 12.6742L8.25833 3.53333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            {registerData.password && (
                                <div className='herrac-register-password-strength'>
                                    <div className='herrac-register-strength-bar'>
                                        <div 
                                            className='herrac-register-strength-fill'
                                            style={{ 
                                                width: `${passwordStrength}%`,
                                                backgroundColor: getPasswordStrengthColor()
                                            }}
                                        ></div>
                                    </div>
                                    <span 
                                        className='herrac-register-strength-text'
                                        style={{ color: getPasswordStrengthColor() }}
                                    >
                                        {getPasswordStrengthText()}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className='herrac-register-input-group'>
                            <label className='herrac-register-label'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M15.8333 9.16667H4.16667C3.24619 9.16667 2.5 9.91286 2.5 10.8333V16.6667C2.5 17.5871 3.24619 18.3333 4.16667 18.3333H15.8333C16.7538 18.3333 17.5 17.5871 17.5 16.6667V10.8333C17.5 9.91286 16.7538 9.16667 15.8333 9.16667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Şifrəni təkrar daxil edin
                            </label>
                            <div className='herrac-register-password-wrapper'>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    value={registerData.confirmPassword}
                                    onChange={handleInputChange}
                                    placeholder='Şifrəni təkrar yazın'
                                    className='herrac-register-input'
                                    autoComplete='new-password'
                                    required
                                    disabled={loading}
                                />
                                <button
                                    type='button'
                                    className='herrac-register-password-toggle'
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    disabled={loading}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M2.5 10C2.5 10 5 4.16667 10 4.16667C15 4.16667 17.5 10 17.5 10C17.5 10 15 15.8333 10 15.8333C5 15.8333 2.5 10 2.5 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                            <path d="M8.33333 8.33333C8.11232 8.65885 7.99609 9.04836 8.00008 9.44605C8.00407 9.84373 8.12806 10.2306 8.35531 10.5515C8.58256 10.8723 8.90151 11.1116 9.2695 11.2372C9.63749 11.3627 10.0346 11.3683 10.4058 11.2533M11.6667 11.6667C11.0844 12.0992 10.3609 12.2822 9.64002 12.178C8.91916 12.0739 8.26431 11.6903 7.80834 11.1066C7.35237 10.5229 7.13046 9.78413 7.19185 9.04086C7.25325 8.29759 7.59299 7.60648 8.14167 7.1M14.95 14.95C13.5255 16.0358 11.788 16.6374 10 16.6667C5 16.6667 2.5 10 2.5 10C3.45651 8.06825 4.81629 6.38051 6.475 5.05L14.95 14.95ZM8.25833 3.53333C8.82361 3.39907 9.4009 3.33195 9.98 3.33333C14.98 3.33333 17.4808 10 17.4808 10C17.0535 10.9463 16.5269 11.8423 15.9108 12.6742L8.25833 3.53333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            <path d="M2.5 2.5L17.5 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {errorMessage && (
                            <div className='herrac-register-error' role='alert'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                    <path d="M10 17.5C14.1421 17.5 17.5 14.1421 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 6.66667V10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M10 13.3333H10.0083" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                {errorMessage}
                            </div>
                        )}

                        <button 
                            type='submit' 
                            className='herrac-register-button'
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className='herrac-register-spinner'></span>
                                    Qeydiyyat edilir...
                                </>
                            ) : (
                                <>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M10 10C12.0711 10 13.75 8.32107 13.75 6.25C13.75 4.17893 12.0711 2.5 10 2.5C7.92893 2.5 6.25 4.17893 6.25 6.25C6.25 8.32107 7.92893 10 10 10Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                        <path d="M3.75 17.5C3.75 14.0482 6.54822 11.25 10 11.25C13.4518 11.25 16.25 14.0482 16.25 17.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Hesab yarat
                                </>
                            )}
                        </button>
                    </form>

                    <div className='herrac-register-divider'>
                        <span>və ya</span>
                    </div>

                    <div className='herrac-register-footer'>
                        <p className='herrac-register-footer-text'>
                            Artıq hesabınız var?
                        </p>
                        <Link to="/giris" className='herrac-register-footer-link'>
                            Daxil olun
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </Link>
                    </div>
                </div>
            </div>

            <ToastContainer
                position='top-center'
                autoClose={3000}
                hideProgressBar={false}
                closeOnClick
                pauseOnHover
                theme='light'
                style={{ zIndex: 99999 }}
            />
        </div>
    );
}

export default Register;